import { useEffect, useState, useMemo } from 'react';
import { Table, Button, Space, Popconfirm, Tag, Select, Spin, Empty, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FilterOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import DOMPurify from 'dompurify';
import { t } from '../../i18n/i18n';
import { SCREENING_STEP_TYPES } from '../../models/screeningCustomStep';
import { getLocalStorageWorkspace } from '../../utils/workspaceUtils';
import ScreeningCustomStepModal from './ScreeningCustomStepModal';
import {
  useScreeningCustomSteps,
  useDeleteScreeningCustomStep,
} from '../../hooks/useScreeningCustomSteps';
import { useRecruitSessionsInfinite, useTestGroupsInfinite } from '../../hooks/useRecruitSessions';
import useTranslations from '../../hooks/useTranslations';
import './ScreeningCustomSteps.scss';
import { parseLineHTML } from '../../utils/parseLineHTML';

const { Option } = Select;

const ScreeningCustomSteps = ({ initialRecruitStage = null }) => {
  const currentWorkspace = getLocalStorageWorkspace();
  const { key: langKey } = useTranslations();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [filters, setFilters] = useState({
    step: null,
    screeningId: initialRecruitStage,
    testGroupGrouperKey: null,
  });
  const [selectedRecruitSession, setSelectedRecruitSession] = useState();

  // React Query hooks
  const {
    data: customSteps = [],
    isLoading: loading,
    error,
    refetch,
  } = useScreeningCustomSteps({
    workspaceId: currentWorkspace?.id,
    ...filters,
  });

  const deleteCustomStepMutation = useDeleteScreeningCustomStep();

  // Fetch recruit sessions (evaluations) with infinite query
  const {
    data: recruitSessionsData,
    isLoading: loadingRecruitSessions,
    hasNextPage: hasNextRecruitSessionsPage,
    fetchNextPage: fetchNextRecruitSessionsPage,
    isFetchingNextPage: isFetchingNextRecruitSessionsPage,
  } = useRecruitSessionsInfinite(currentWorkspace?.id, {
    status: 'IN_PROGRESS',
    ...(initialRecruitStage && { screeningId: initialRecruitStage }),
  });

  // Flatten all recruit sessions from infinite query pages
  const recruitSessions = useMemo(() => {
    if (!recruitSessionsData?.pages) return [];
    return recruitSessionsData.pages
      .flatMap((page) => {
        if (Array.isArray(page)) {
          return page;
        }
        if (page?.collection) {
          return page.collection;
        }
        if (page) {
          return [page];
        }
        return [];
      })
      .filter(
        (session) => session && (session.id || session.recruitSessionStageId || session.levelId)
      );
  }, [recruitSessionsData]);

  // Fetch test groups with infinite query
  const {
    data: testGroupsData,
    isLoading: loadingTestGroups,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useTestGroupsInfinite(
    {
      levelId:
        selectedRecruitSession?.stages?.[0]?.levelId ||
        selectedRecruitSession?.levelId ||
        selectedRecruitSession?.screeningId ||
        null,
      workspaceId: currentWorkspace?.id,
    },
    {
      enabled: true,
    }
  );

  // Flatten all test groups from infinite query pages
  const testGroups = useMemo(() => {
    if (!testGroupsData?.pages) return [];
    return testGroupsData.pages.flatMap((page) => page?.collection || []);
  }, [testGroupsData]);

  // Load more test groups when dropdown is scrolled
  const handleTestGroupsScroll = (e) => {
    const { target } = e;
    if (
      target.scrollTop + target.offsetHeight === target.scrollHeight &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  // Load more recruit sessions when dropdown is scrolled
  const handleRecruitSessionsScroll = (e) => {
    const { target } = e;
    if (
      target.scrollTop + target.offsetHeight === target.scrollHeight &&
      hasNextRecruitSessionsPage &&
      !isFetchingNextRecruitSessionsPage
    ) {
      fetchNextRecruitSessionsPage();
    }
  };

  const handleCreate = () => {
    setEditingStep(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingStep(record);
    setModalVisible(true);
  };

  const handleDelete = (id) => {
    deleteCustomStepMutation.mutate(id);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingStep(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    refetch();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (initialRecruitStage && recruitSessions.length > 0) {
      const matchingSession = recruitSessions.find(
        (s) =>
          s.id === initialRecruitStage ||
          s.recruitSessionId === initialRecruitStage ||
          s.recruitSessionStageId === initialRecruitStage ||
          s.screeningId === initialRecruitStage ||
          s.levelId === initialRecruitStage
      );
      if (matchingSession && !selectedRecruitSession) {
        setSelectedRecruitSession(matchingSession);
        const screeningId =
          matchingSession.screeningId ||
          matchingSession.levelId ||
          matchingSession.stages?.[0]?.levelId ||
          null;
        if (screeningId) {
          handleFilterChange('screeningId', screeningId);
        }
      }
    }
  }, [recruitSessions, initialRecruitStage]);

  const handleRecruitSessionChange = (value) => {
    if (!value) {
      setSelectedRecruitSession(null);
      handleFilterChange('screeningId', null);
      handleFilterChange('levelId', null);
      return;
    }

    const selectedSession = recruitSessions.find(
      (s) =>
        s.id === value ||
        s.recruitSessionId === value ||
        s.recruitSessionStageId === value ||
        s.screeningId === value ||
        s.levelId === value
    );
    setSelectedRecruitSession(selectedSession);

    if (selectedSession) {
      const screeningId =
        selectedSession.screeningId ||
        selectedSession.levelId ||
        selectedSession.stages?.[0]?.levelId ||
        selectedSession.id;
      handleFilterChange('screeningId', screeningId);
      handleFilterChange('levelId', selectedSession?.stages?.[0]?.levelId);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      step: null,
      screeningId: null,
      testGroupGrouperKey: null,
    });
    setSelectedRecruitSession(null);
  };

  const handleRetry = () => {
    refetch();
  };

  const getStepTypeColor = (step) => {
    switch (step) {
      case SCREENING_STEP_TYPES.START:
        return 'green';
      case SCREENING_STEP_TYPES.SETTINGS:
        return 'blue';
      case SCREENING_STEP_TYPES.FINAL:
        return 'orange';
      default:
        return 'default';
    }
  };

  const getTestGroupName = (customStep) => {
    // Use populated testGroup data if available
    if (customStep.testGroup) {
      return (
        customStep.testGroup.nameTranslated?.[langKey] ||
        customStep.testGroup.name ||
        customStep.testGroup.grouperKey
      );
    }
    // Fallback to finding test group by grouperKey
    const testGroup = testGroups.find((tg) => tg.grouperKey === customStep.testGroupGrouperKey);
    return (
      testGroup?.nameTranslated?.[langKey] || testGroup?.name || customStep.testGroupGrouperKey
    );
  };

  const getScreeningName = (customStep) => {
    if (customStep.screening) {
      return (
        customStep.screening.nameTranslated?.[langKey] ||
        customStep.screening.name ||
        customStep.screening.id
      );
    }
    const session = recruitSessions.find(
      (rs) =>
        rs.screeningId === customStep.screeningId ||
        rs.levelId === customStep.screeningId ||
        rs.id === customStep.screeningId ||
        rs.recruitSessionStageId === customStep.screeningId
    );
    return (
      session?.recruitSessionStageName ||
      session?.nameTranslated?.[langKey] ||
      session?.name ||
      customStep.screeningId
    );
  };

  const columns = [
    {
      title: t('SCREENING_CUSTOM_STEP_TYPE'),
      dataIndex: 'step',
      key: 'step',
      width: 150,
      render: (step) => (
        <Tag color={getStepTypeColor(step)}>{t(`SCREENING_STEP_TYPE_${step}`)}</Tag>
      ),
    },
    {
      title: t('SCREENING_CUSTOM_STEP_SUBTITLE'),
      dataIndex: 'subtitle',
      key: 'subtitle',
      width: 300,
      ellipsis: true,
      render: (subtitle) => {
        let data;
        if (typeof subtitle === 'object' && subtitle !== null) {
          data = subtitle.es || subtitle.en || subtitle.pt || '-';
        } else {
          data = subtitle;
        }
        let content = DOMPurify.sanitize(parseLineHTML(data)) ?? '-';
        content = `${content.slice(0, 300)}...`;
        return <div dangerouslySetInnerHTML={{ __html: content }} />;
      },
    },
    {
      title: t('SCREENING_CUSTOM_STEP_SCREENING'),
      key: 'screening',
      width: 250,
      render: (_, record) => {
        if (record.screeningId) {
          const screeningName = getScreeningName(record);
          return (
            <Tag color="blue" title={`ID: ${record.screeningId}`}>
              {screeningName}
            </Tag>
          );
        }
        return (
          <Tag color="purple" title={t('SCREENING_CUSTOM_STEP_APPLIES_ALL_EVALUATIONS')}>
            {t('SCREENING_CUSTOM_STEP_APPLIES_ALL_EVALUATIONS')}
          </Tag>
        );
      },
    },
    {
      title: t('SCREENING_CUSTOM_STEP_TEST_GROUP'),
      key: 'testGroup',
      width: 250,
      render: (_, record) => {
        if (record.testGroupGrouperKey) {
          const testGroupName = getTestGroupName(record);
          return (
            <Tag color="cyan" title={`Key: ${record.testGroupGrouperKey}`}>
              {testGroupName}
            </Tag>
          );
        }
        return (
          <Tag color="purple" title={t('SCREENING_CUSTOM_STEP_APPLIES_ALL_SECTIONS')}>
            {t('SCREENING_CUSTOM_STEP_APPLIES_ALL_SECTIONS')}
          </Tag>
        );
      },
    },
    {
      title: t('SCREENING_CUSTOM_STEP_ACTIONS'),
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title={t('SCREENING_CUSTOM_STEP_DELETE_CONFIRM')}
            onConfirm={() => handleDelete(record._id)}
            okText={t('YES')}
            cancelText={t('NO')}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={deleteCustomStepMutation.isLoading}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="screening-custom-steps">
      <div className="screening-custom-steps__header">
        <h2>{t('SCREENING_CUSTOM_STEPS_TITLE')}</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          {t('SCREENING_CUSTOM_STEP_CREATE')}
        </Button>
      </div>

      <div className="screening-custom-steps__filters">
        <Space wrap>
          <Select
            style={{ width: 200 }}
            placeholder={t('SCREENING_CUSTOM_STEP_FILTER_TYPE')}
            value={filters.step}
            onChange={(value) => handleFilterChange('step', value)}
            allowClear
          >
            {Object.values(SCREENING_STEP_TYPES).map((type) => (
              <Option key={type} value={type}>
                {t(`SCREENING_STEP_TYPE_${type}`)}
              </Option>
            ))}
          </Select>

          <Select
            style={{ width: 250 }}
            placeholder={t('SCREENING_CUSTOM_STEP_FILTER_EVALUATION')}
            value={
              selectedRecruitSession?.id ||
              selectedRecruitSession?.recruitSessionId ||
              selectedRecruitSession?.recruitSessionStageId ||
              selectedRecruitSession?.screeningId
            }
            onChange={handleRecruitSessionChange}
            loading={loadingRecruitSessions}
            allowClear
            showSearch
            onPopupScroll={handleRecruitSessionsScroll}
            filterOption={(input, option) => {
              const searchText = option?.label || '';
              return searchText.toLowerCase().includes(input.toLowerCase());
            }}
            dropdownRender={(menu) => (
              <>
                {menu}
                {isFetchingNextRecruitSessionsPage && (
                  <div style={{ textAlign: 'center', padding: '8px' }}>
                    <Spin size="small" />
                  </div>
                )}
              </>
            )}
          >
            {recruitSessions.map((session) => {
              const sessionId = session.id || session.recruitSessionId;
              const sessionName =
                session.recruitSessionStageName ||
                session.nameTranslated?.[langKey] ||
                session.name;
              return (
                <Option key={sessionId} value={sessionId} label={sessionName}>
                  {sessionName}
                </Option>
              );
            })}
          </Select>

          <Select
            style={{ width: 250 }}
            placeholder={t('SCREENING_CUSTOM_STEP_FILTER_TEST_GROUP')}
            value={filters.testGroupGrouperKey}
            onChange={(value) => handleFilterChange('testGroupGrouperKey', value)}
            loading={loadingTestGroups}
            allowClear
            showSearch
            onPopupScroll={handleTestGroupsScroll}
            filterOption={(input, option) => {
              const searchText = option?.label || '';
              return searchText.toLowerCase().includes(input.toLowerCase());
            }}
            dropdownRender={(menu) => (
              <>
                {menu}
                {isFetchingNextPage && (
                  <div style={{ textAlign: 'center', padding: '8px' }}>
                    <Spin size="small" />
                  </div>
                )}
              </>
            )}
          >
            {testGroups.map((testGroup) => (
              <Option
                key={testGroup.grouperKey || testGroup.id}
                value={testGroup.grouperKey || testGroup.id}
                label={testGroup.name}
              >
                {testGroup.name}
              </Option>
            ))}
          </Select>

          <Button icon={<FilterOutlined />} onClick={handleClearFilters}>
            {t('CLEAR_FILTERS')}
          </Button>
        </Space>
      </div>

      <div className="screening-custom-steps__table">
        <Table
          columns={columns}
          dataSource={customSteps}
          loading={loading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => t('TOTAL_RECORDS').replace('{{total}}', total),
          }}
          scroll={{ x: 1200 }}
          locale={{
            emptyText: <Empty description={t('SCREENING_CUSTOM_STEPS_EMPTY')} />,
            items_per_page: `/ ${t('PAGE')}`,
            jump_to: t('JUMP_TO'),
            page: t('PAGE'),
          }}
        />
      </div>

      {modalVisible && (
        <ScreeningCustomStepModal
          visible={modalVisible}
          customStep={editingStep}
          recruitSessions={recruitSessions}
          testGroups={testGroups}
          workspaceId={currentWorkspace?.id}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          onRecruitSessionsScroll={handleRecruitSessionsScroll}
          isFetchingMoreRecruitSessions={isFetchingNextRecruitSessionsPage}
        />
      )}

      {error && !loading && (
        <div className="screening-custom-steps__error">
          <Alert
            message={t('SCREENING_CUSTOM_STEPS_LOAD_ERROR')}
            description={
              <div>
                <p>{error.message}</p>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRetry}
                  size="small"
                  style={{ marginTop: 8 }}
                >
                  {t('RETRY')}
                </Button>
              </div>
            }
            type="error"
            showIcon
          />
        </div>
      )}
    </div>
  );
};

export default ScreeningCustomSteps;
